import React, { useState } from 'react';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';
import { FriendRequestEntry, AcceptFriendRequestWithLimits } from '../api/friends';

interface FriendRequestApprovalModalProps {
  request: FriendRequestEntry | null;
  isOpen: boolean;
  onClose: () => void;
  onAccept: (requestId: string, limits?: AcceptFriendRequestWithLimits) => Promise<void>;
  onDecline: (requestId: string) => Promise<void>;
}

const FriendRequestApprovalModal: React.FC<FriendRequestApprovalModalProps> = ({
  request,
  isOpen,
  onClose,
  onAccept,
  onDecline,
}) => {
  const [autoApprove, setAutoApprove] = useState(false);
  const [bandwidthLimit, setBandwidthLimit] = useState<string>('');
  const [dataLimit, setDataLimit] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen || !request) return null;

  const handleAccept = async () => {
    setIsProcessing(true);
    try {
      const limits: AcceptFriendRequestWithLimits = {
        auto_approve_connections: autoApprove,
        bandwidth_limit_mbps: bandwidthLimit ? parseInt(bandwidthLimit) : undefined,
        daily_data_limit_gb: dataLimit ? parseInt(dataLimit) : undefined,
      };

      await onAccept(request.request_id, limits);
      handleClose();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    setIsProcessing(true);
    try {
      await onDecline(request.request_id);
      handleClose();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setAutoApprove(false);
    setBandwidthLimit('');
    setDataLimit('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={handleClose}
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Accept Friend Request
              </h3>

              {/* Friend Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-lg font-bold text-blue-600">
                        {request.friend.display_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {request.friend.display_name}
                    </div>
                    {request.friend.username && (
                      <div className="text-sm text-gray-500">
                        @{request.friend.username}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Security Warning */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Security Notice
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        By accepting this friend request, you allow {request.friend.display_name} to:
                      </p>
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>See when you're online</li>
                        <li>Send you connection requests</li>
                        <li>View your sharing status</li>
                      </ul>
                      <p className="mt-2 font-medium">
                        Only accept requests from people you trust.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Limit Controls */}
              <div className="space-y-4 mb-4">
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Optional Connection Limits
                  </h4>

                  {/* Auto-approve checkbox */}
                  <div className="flex items-start mb-4">
                    <div className="flex items-center h-5">
                      <input
                        id="auto-approve"
                        type="checkbox"
                        checked={autoApprove}
                        onChange={(e) => setAutoApprove(e.target.checked)}
                        className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="auto-approve" className="font-medium text-gray-700">
                        Auto-approve connection requests
                      </label>
                      <p className="text-gray-500">
                        Automatically accept when they want to connect through your VPN
                      </p>
                    </div>
                  </div>

                  {/* Bandwidth Limit */}
                  <div className="mb-4">
                    <label htmlFor="bandwidth-limit" className="block text-sm font-medium text-gray-700 mb-1">
                      Bandwidth Limit (Mbps)
                    </label>
                    <input
                      id="bandwidth-limit"
                      type="number"
                      min="1"
                      value={bandwidthLimit}
                      onChange={(e) => setBandwidthLimit(e.target.value)}
                      placeholder="Unlimited"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Leave empty for unlimited bandwidth
                    </p>
                  </div>

                  {/* Data Limit */}
                  <div className="mb-4">
                    <label htmlFor="data-limit" className="block text-sm font-medium text-gray-700 mb-1">
                      Daily Data Limit (GB)
                    </label>
                    <input
                      id="data-limit"
                      type="number"
                      min="1"
                      value={dataLimit}
                      onChange={(e) => setDataLimit(e.target.value)}
                      placeholder="Unlimited"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Leave empty for unlimited daily data usage
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-md p-3">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ShieldExclamationIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-xs text-gray-600">
                          <strong>Tip:</strong> Set limits to protect your bandwidth and data. You can always adjust these later in your friend settings.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse space-y-3 sm:space-y-0 sm:space-x-3 sm:space-x-reverse">
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={handleAccept}
                  className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  <CheckIcon className="h-5 w-5 mr-2" />
                  {isProcessing ? 'Processing...' : 'Accept Friend Request'}
                </button>
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={handleDecline}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Decline
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FriendRequestApprovalModal;
